import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SortAscendingOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { observer } from "mobx-react";
import { useCallback, useRef, useState } from "react";
import { useFolderTreeSort } from "@devowl-wp/react-folder-tree";
import { useStores } from "../../store/rootStore.js";
import { cloneNodesFlat, createAuxiliaryTreeStore } from "../../store/treeStore.js";
import { __ } from "../../utils/i18n.js";
import { getRmlModalStackProps } from "../../utils/rmlModalStack.js";
import { FolderBox, FolderBoxHeader } from "../media/folderBox.js";
import { FolderSelector } from "../media/folderSelector.js";
const RearrangeBox = observer(({ id: rearrangeId, onClose })=>{
    const onSort = useFolderTreeSort();
    const { treeStore: store } = useStores();
    const parentStoreRef = useRef(createAuxiliaryTreeStore());
    const siblingStoreRef = useRef(createAuxiliaryTreeStore());
    const parentStore = parentStoreRef.current;
    const siblingStore = siblingStoreRef.current;
    const [loading, setLoading] = useState(false);
    const handleSelect = useCallback((parent)=>{
        const source = parent ? parent.childNodes : store.tree;
        siblingStore.setTree(cloneNodesFlat(source, siblingStore));
        siblingStore.selected?.setSelected(false);
    }, [
        store,
        siblingStore
    ]);
    const handleSave = useCallback(async ()=>{
        setLoading(true);
        try {
            const item = parentStore.getTreeItemById(rearrangeId, false);
            if (!item) {
                return;
            }
            const id = item.id;
            const oldItem = store.getTreeItemById(id);
            const parentToId = parentStore.selected ? parentStore.selectedId : store.rootId;
            const nextId = siblingStore.selectedId || NaN;
            const sortProps = {
                id,
                oldIndex: oldItem.parentArray.indexOf(oldItem),
                parentFromId: oldItem.parentId,
                parentToId,
                nextId,
                newIndex: 0
            };
            const parentTo = store.getTreeItemById(parentToId);
            const newIndexRelative = parentTo ? parentTo.childNodes : store.tree;
            const newIndexA = newIndexRelative.filter((node)=>node.id !== oldItem.id).map((node, idx)=>node.id === nextId ? `${idx}` : null).filter(Boolean).map((index)=>+index);
            sortProps.newIndex = !isNaN(nextId) && newIndexA.length ? newIndexA[0] : newIndexRelative.length;
            const result = await onSort({
                ...sortProps,
                source: "dialog"
            });
            if (result !== false) {
                onClose();
            }
        } finally{
            setLoading(false);
        }
    }, [
        onClose,
        onSort,
        parentStore,
        rearrangeId,
        siblingStore,
        store
    ]);
    const handleNodeInit = useCallback((itemId)=>{
        const item = parentStore.getTreeItemById(itemId, false);
        if (!item) {
            return;
        }
        const { nextSibling, parentArray } = item;
        const nextId = nextSibling ? nextSibling.id : undefined;
        siblingStore.setTree(cloneNodesFlat(parentArray, siblingStore));
        parentStore.getTreeItemById(item.id)?.setter((n)=>n.$visible = false);
        siblingStore.getTreeItemById(item.id)?.setter((n)=>n.$visible = false);
        if (nextId !== undefined) {
            siblingStore.getTreeItemById(nextId)?.setSelected(true);
        }
    }, [
        parentStore,
        siblingStore
    ]);
    let modalContent;
    let title;
    const selected = rearrangeId && store.getTreeItemById(rearrangeId, false);
    if (selected) {
        const selectedId = typeof selected.parentId === "number" && selected.parentId > 0 ? selected.parentId : "";
        title = /*#__PURE__*/ _jsx(FolderBoxHeader, {
            icon: /*#__PURE__*/ _jsx(SortAscendingOutlined, {}),
            folder: selected
        });
        modalContent = /*#__PURE__*/ _jsx(FolderBox, {
            busy: false,
            icon: undefined,
            folder: selected,
            header: false,
            children: /*#__PURE__*/ _jsxs("form", {
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        children: __("Parent")
                    }),
                    /*#__PURE__*/ _jsx(FolderSelector, {
                        tree: parentStore,
                        selected: selectedId,
                        nullable: true,
                        disabled: "4",
                        onSelect: handleSelect,
                        onNodeInit: ()=>handleNodeInit(rearrangeId)
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        children: __("Before this node")
                    }),
                    /*#__PURE__*/ _jsx(FolderSelector, {
                        tree: siblingStore,
                        fetch: false,
                        nullable: true,
                        disabled: "4",
                        ignoreChildNodes: true
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        children: __("If no next node is given, the element is placed at the end.")
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(Modal, {
        open: !!modalContent,
        title: title,
        onOk: handleSave,
        onCancel: onClose,
        cancelText: __("Cancel"),
        okText: __("Save"),
        confirmLoading: loading,
        ...getRmlModalStackProps(),
        children: modalContent
    });
});
export { RearrangeBox };
